import type { GeoJsonProperties } from 'geojson';
import type { MapView } from './types';
import {
  type EventDetailFilter,
  fetchEventDetail,
  fetchSnapshotTimes,
  fetchFeatureStats,
} from './featuresApi';
import { WOW_DATE_FIELD } from './endpoints/wow';
import { NIFC_DATE_FIELD } from './endpoints/nifc';
import { CANOPY_DATE_FIELD } from './endpoints/canopy';
import { TING_DATE_FIELD } from './endpoints/ting';

export type { MapView } from './types';

/** The history table holding each feed's event polygons, one row per snapshot. */
export const THREAT_HISTORY_TABLE: Record<MapView, string> = {
  wow: 'wow_threats_history',
  nifc: 'nifc_bounds_history',
  canopy: 'canopy_swaths_history',
  ting: 'ting_outages_history',
};

/** The column of each feed's history rows that stamps the snapshot they belong to. */
export const DETAIL_TIMESTAMP_FIELD: Record<MapView, string> = {
  wow: WOW_DATE_FIELD,
  nifc: NIFC_DATE_FIELD,
  canopy: CANOPY_DATE_FIELD,
  ting: TING_DATE_FIELD,
};

/** The column identifying one event's rows in its threat-history table. */
export const DETAIL_ID_FIELD: Record<MapView, string> = {
  wow: 'threat_id',
  nifc: 'incidentname',
  canopy: 'kml_id',
  ting: 'event_id',
};

/**
 * The column values that identify one event's rows in its threat-history
 * table, most selective first: the leading column is the one a lookup groups
 * (DISTINCT ON) by. A WoW forecast product (winter weather, an outlook, an
 * accumulation, a probability) carries no threat_id: it is the category's
 * polygon over one county list, so those are its identity, and a null value
 * means the column is null on its rows.
 */
export function eventIdentity(
  mapView: MapView,
  row: NonNullable<GeoJsonProperties>
): Record<string, string | null> {
  const value = (field: string): string | null => {
    const raw: unknown = row[field];
    return raw == null ? null : String(raw);
  };
  // Same edge as fetchWowEventDetails' grouping: an absent or empty threat_id
  // marks a forecast product row.
  const wowThreatId = value('threat_id');
  if (mapView === 'wow' && (wowThreatId == null || wowThreatId.length === 0)) {
    return {
      county_intersect: value('county_intersect'),
      category: value('category'),
      threat_id: null,
    };
  }
  const idField = DETAIL_ID_FIELD[mapView];
  return { [idField]: value(idField) };
}

/**
 * The newest snapshot of the feed's history that holds the given event, i.e.
 * the greatest snapshot stamp among the event's history rows, or null when it
 * has none. The event's own attributes are no guide to that time -- a NIFC
 * perimeter's createdate predates every snapshot of it, and a Ting outage is
 * archived only at the polls where it changed -- so the history table is asked.
 */
export async function fetchLatestEventSnapshot(
  mapView: MapView,
  properties: NonNullable<GeoJsonProperties>,
  signal?: AbortSignal
): Promise<Date | null> {
  const identity = eventIdentity(mapView, properties);
  // An identity of only nulls names no event: filtering on it would return the
  // newest of every row missing those columns.
  if (Object.values(identity).every(value => value == null)) {
    throw new Error(`${mapView} event has no ${Object.keys(identity).join(', ')}`);
  }
  const dateField = DETAIL_TIMESTAMP_FIELD[mapView];
  // Every filter pins its column to one value (or to NULL, which DISTINCT ON
  // also treats as one group), so the matching rows form a single group and
  // the query returns just its newest row. Grouping on the leading identity
  // column keeps a forecast lookup in its index's order; an observed WoW
  // threat's index puts the county between threat_id and the stamp, so that
  // lookup sorts the one threat's rows the index seek returns.
  const [distinctOn] = Object.keys(identity);
  const rows = await fetchEventDetail(THREAT_HISTORY_TABLE[mapView], {
    distinctOn,
    orderBy: dateField,
    filters: Object.entries(identity).map(([field, value]): EventDetailFilter =>
      value == null ? { field, isNull: true } : { field, value }
    ),
    fields: [dateField],
    signal,
  });
  if (rows.length === 0) {
    return null;
  }
  const stamp = rows[0][dateField];
  const snapshot = stamp == null ? Number.NaN : new Date(stamp).getTime();
  if (!Number.isFinite(snapshot)) {
    throw new Error(
      `${THREAT_HISTORY_TABLE[mapView]} row for ${JSON.stringify(identity)} has no ${dateField}`
    );
  }
  return new Date(snapshot);
}

// Detail fields fetched lazily for a single clicked chart point (geometry
// excluded). These populate the click-through dropdown in WeatherEventScatter.
const WOW_STATS_FIELDS: string[] = [
  'category',
  'categoryid',
  'datasource',
  'lastupdatedon',
  'threat',
  'threatdescription',
  'threatlevel',
  'county_intersect',
  'initial_date',
  'residential_struc_exposed',
  'state_intersect',
  'structures_exposed',
  'threat_id',
  'time_category',
];

const NIFC_STATS_FIELDS: string[] = ['incidentname', 'run_time'];

const CANOPY_STATS_FIELDS: string[] = [
  'event_name',
  'created_at',
  'ef_rank',
  'kml_id',
  'canopy_published_date',
  'structures_exposed',
  'county_intersect',
  'state_intersect',
  'fips_intersect',
  'residential_struc_exposed',
  'initial_date',
];

const TING_STATS_FIELDS: string[] = [
  'event_id',
  'vendor_start',
  'vendor_modified',
  'power_state',
  'recovered_percentage',
  'structures_exposed',
  'residential_struc_exposed',
  'county_intersect',
  'state_intersect',
  'run_time',
];

const SIXTY_DAYS = 60;

function sixtyDaysAgo(): Date {
  const date = new Date();
  date.setDate(date.getDate() - SIXTY_DAYS);
  return date;
}

// A single dot in the time-series scatter: one entity (threat/incident/swath)
// produced by a server-side GROUP BY, not an individual history row. `raw`
// carries the entity key(s) needed to lazily refetch the entity's detail.
export type ChartPoint = {
  /** Epoch milliseconds, used for the X axis. */
  timestamp: number;
  /** Y-axis category label. */
  yGroup: string;
  raw: Record<string, string | number | null>;
};

export type WeatherEventDetail = Record<string, string | number | null>;

export async function fetchWowStats(): Promise<ChartPoint[]> {
  // AGOL parity: one dot per threat (per category + intersected county), windowed
  // by the event date initial_date, positioned at the threat's latest update.
  const rows = await fetchFeatureStats('wow_threats_history', {
    groupBy: ['threat_id', 'category', 'county_intersect'],
    statistics: 'max:lastupdatedon',
    dateField: 'initial_date',
    from: sixtyDaysAgo(),
  });

  return rows
    .map((r): ChartPoint | null => {
      const timestamp = new Date(r.lastupdatedon as string).getTime();
      if (!Number.isFinite(timestamp)) return null;
      return {
        timestamp,
        yGroup: String(r.category),
        raw: { threat_id: r.threat_id, county_intersect: r.county_intersect },
      };
    })
    .filter((p): p is ChartPoint => p !== null);
}

export async function fetchNifcStats(): Promise<ChartPoint[]> {
  // AGOL parity: one dot per incident, windowed and positioned by createdate (the
  // incident's creation date). Click-navigation still targets run_time.
  const rows = await fetchFeatureStats('nifc_bounds_history', {
    groupBy: ['incidentname'],
    statistics: 'max:createdate',
    dateField: 'createdate',
    from: sixtyDaysAgo(),
  });

  return rows
    .map((r): ChartPoint | null => {
      const timestamp = new Date(r.createdate as string).getTime();
      if (!Number.isFinite(timestamp)) return null;
      return { timestamp, yGroup: 'NIFC', raw: { incidentname: r.incidentname } };
    })
    .filter((p): p is ChartPoint => p !== null);
}

export async function fetchCanopyStats(): Promise<ChartPoint[]> {
  // AGOL parity: one dot per swath (kml_id), windowed and positioned by the event
  // date initial_date, grouped on the Y axis by EF rank.
  const rows = await fetchFeatureStats('canopy_swaths_history', {
    groupBy: ['kml_id'],
    statistics: 'max:initial_date,max:ef_rank',
    dateField: 'initial_date',
    from: sixtyDaysAgo(),
  });

  return rows
    .map((r): ChartPoint | null => {
      const timestamp = new Date(r.initial_date as string).getTime();
      if (!Number.isFinite(timestamp)) return null;
      const rank = r.ef_rank as number | null;
      return {
        timestamp,
        // ef_rank is a nullable column; an unrated swath gets its own labelled
        // row instead of rendering as the literal "EFundefined".
        yGroup: rank == null ? 'Unrated' : `EF${rank}`,
        raw: { kml_id: r.kml_id },
      };
    })
    .filter((p): p is ChartPoint => p !== null);
}

export async function fetchTingStats(): Promise<ChartPoint[]> {
  // One dot per outage event, positioned at the poll where the event was first
  // archived (min run_time) rather than its latest change: ting_outages_history
  // is change-based, so the latest change for almost every live outage is the
  // most recent poll, which would pile every dot onto today and leave the date
  // picker with a single selectable day.
  const rows = await fetchFeatureStats('ting_outages_history', {
    groupBy: ['event_id', 'power_state'],
    statistics: 'min:run_time',
    dateField: 'run_time',
    from: sixtyDaysAgo(),
  });

  return rows
    .map((r): ChartPoint | null => {
      const timestamp = new Date(r.run_time as string).getTime();
      if (!Number.isFinite(timestamp)) return null;
      const state = r.power_state;
      return {
        timestamp,
        // power_state is nullable; an unreported status gets its own labelled
        // row instead of rendering as "null".
        yGroup: state == null ? 'Unknown Status' : String(state),
        raw: { event_id: r.event_id },
      };
    })
    .filter((p): p is ChartPoint => p !== null);
}

export async function fetchWeatherStats(mapView: MapView = 'wow'): Promise<ChartPoint[]> {
  switch (mapView) {
    case 'wow':
      return fetchWowStats();
    case 'nifc':
      return fetchNifcStats();
    case 'canopy':
      return fetchCanopyStats();
    case 'ting':
      return fetchTingStats();
  }
}

async function fetchWowEventDetails(points: ChartPoint[]): Promise<WeatherEventDetail[]> {
  // Group clicked dots by entity -- threat_id for threats, category for forecast
  // dots (winter weather, outlooks, accumulations, probabilities) which carry no
  // threat_id -- collecting each group's clicked counties, so a clump of
  // overlapping county dots resolves to a single query.
  type WowGroup = { byThreatId: boolean; key: string; counties: (string | null)[] };
  const groups = new Map<string, WowGroup>();
  for (const point of points) {
    const threatId = point.raw.threat_id;
    const byThreatId = threatId != null && String(threatId).length > 0;
    const key = byThreatId ? String(threatId) : point.yGroup;
    const groupId = `${byThreatId ? 't' : 'c'}:${key}`;
    const group: WowGroup = groups.get(groupId) ?? { byThreatId, key, counties: [] };
    const county = point.raw.county_intersect;
    group.counties.push(county != null && String(county).length > 0 ? String(county) : null);
    groups.set(groupId, group);
  }

  const perGroup = await Promise.all(
    Array.from(groups.values()).map(group => {
      const filters: EventDetailFilter[] = group.byThreatId
        ? [{ field: 'threat_id', value: group.key }]
        : [
            { field: 'category', value: group.key },
            { field: 'threat_id', isNull: true },
          ];
      const namedCounties = group.counties.filter((c): c is string => c !== null);
      if (namedCounties.length > 0) {
        filters.push({
          field: 'county_intersect',
          in: namedCounties,
          includeNull: group.counties.some(c => c === null),
        });
      } else {
        filters.push({ field: 'county_intersect', isNull: true });
      }
      return fetchEventDetail('wow_threats_history', {
        distinctOn: 'county_intersect',
        orderBy: 'lastupdatedon',
        filters,
        fields: WOW_STATS_FIELDS,
      });
    })
  );
  return perGroup.flat();
}

async function fetchNifcEventDetails(points: ChartPoint[]): Promise<WeatherEventDetail[]> {
  const incidentNames = Array.from(new Set(points.map(p => String(p.raw.incidentname))));
  return fetchEventDetail('nifc_bounds_history', {
    distinctOn: 'incidentname',
    orderBy: 'run_time',
    filters: [{ field: 'incidentname', in: incidentNames }],
    fields: NIFC_STATS_FIELDS,
  });
}

async function fetchCanopyEventDetails(points: ChartPoint[]): Promise<WeatherEventDetail[]> {
  const kmlIds = Array.from(new Set(points.map(p => String(p.raw.kml_id))));
  return fetchEventDetail('canopy_swaths_history', {
    distinctOn: 'kml_id',
    orderBy: 'created_at',
    filters: [{ field: 'kml_id', in: kmlIds }],
    fields: CANOPY_STATS_FIELDS,
  });
}

async function fetchTingEventDetails(points: ChartPoint[]): Promise<WeatherEventDetail[]> {
  const eventIds = Array.from(new Set(points.map(p => String(p.raw.event_id))));
  return fetchEventDetail('ting_outages_history', {
    distinctOn: 'event_id',
    // vendor_modified, not run_time: it is the version key of the change-based
    // archive, and (event_id, vendor_modified) is exactly
    // idx_ting_outages_history_event_id_vendor_modified.
    orderBy: 'vendor_modified',
    filters: [{ field: 'event_id', in: eventIds }],
    fields: TING_STATS_FIELDS,
  });
}

// Lazily fetch the underlying records for the clicked chart dot(s). The chart
// itself only loads aggregated buckets, so this runs on demand per click; the
// server returns the latest row per clicked entity in one indexed query.
export async function fetchWeatherEventDetails(
  mapView: MapView,
  points: ChartPoint[]
): Promise<WeatherEventDetail[]> {
  if (points.length === 0) {
    return [];
  }
  switch (mapView) {
    case 'wow':
      return fetchWowEventDetails(points);
    case 'nifc':
      return fetchNifcEventDetails(points);
    case 'canopy':
      return fetchCanopyEventDetails(points);
    case 'ting':
      return fetchTingEventDetails(points);
  }
}

const DATE_TABLE_MAP: Record<MapView, { table: string; dateField: string }> = {
  wow: { table: 'wow_post_history', dateField: WOW_DATE_FIELD },
  nifc: { table: 'nifc_post_history', dateField: NIFC_DATE_FIELD },
  canopy: { table: 'canopy_post_history', dateField: CANOPY_DATE_FIELD },
  // ting_outages_history is change-based, so each distinct run_time is the poll
  // at which some (event_id, vendor_modified) version was first observed. A
  // slider mark therefore steps to a batch of newly observed versions, not to a
  // full snapshot of the outages active at that instant.
  ting: { table: 'ting_outages_history', dateField: TING_DATE_FIELD },
};

export async function fetchWeatherDates<T extends MapView>(
  mapView: T = 'wow' as T,
  selectedDate: Date,
  signal?: AbortSignal
): Promise<Date[]> {
  const config = DATE_TABLE_MAP[mapView];

  // Snapshot times for the selected day only: the slider steps between the
  // snapshots within that day, and bounding the query keeps it to a day-sized
  // index scan rather than scanning all history from the day forward.
  const dayStart = new Date(selectedDate);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(selectedDate);
  dayEnd.setHours(23, 59, 59, 999);

  const isoStrings = await fetchSnapshotTimes(
    config.table,
    config.dateField,
    dayStart,
    dayEnd,
    signal
  );

  return isoStrings.map(s => new Date(s)).sort((a, b) => b.getTime() - a.getTime());
}
